// Dateien speichern – Eingang für Module (@api). Kern (Browser-Download bzw. Teilen-Blatt der App): web/ui/files.ts.
// Hier dazu: Dateien des eigenen Servers mit der Sitzung holen (saveUrl, DownloadLink) und neue Tabs (openInNewTab).
import { forwardRef, type AnchorHTMLAttributes, type MouseEvent, type ReactNode } from 'react'
import { nativeApp } from '../lib/native'
import { openInNewWindow, SAVE_FILE_MESSAGES, saveFile, type SaveFileOutcome } from '../ui/files'
import { toast } from '../ui/Toast'

export { saveFile, type SaveFileData, type SaveFileOptions, type SaveFileOutcome } from '../ui/files'

const fileNameFrom = (disposition: string | null, fallback: string): string => {
  if (!disposition) return fallback
  const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition)
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1])
    } catch {
      // ungültige Kodierung – einfacher Name unten
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition)
  return plain?.[1] ?? fallback
}

/**
 * Datei vom eigenen Server (z. B. /api/files/<id>) mit der Sitzung holen und speichern – in der App, wo ein
 * Verweis mit download-Attribut nichts täte. Im Browser lädt der Verweis selbst (DownloadLink).
 */
export async function saveUrl(url: string, options: { fileName?: string; anchor?: Element | null } = {}): Promise<SaveFileOutcome> {
  try {
    const target = new URL(url, window.location.origin)
    if (target.origin !== window.location.origin) throw new Error('fremde Herkunft')
    if (!target.searchParams.has('download')) target.searchParams.set('download', '1')
    const response = await fetch(target.toString(), { credentials: 'same-origin' })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    const blob = await response.blob()
    const fallback = target.pathname.split('/').pop() || 'ordaneo-datei'
    return await saveFile({
      fileName: options.fileName ?? fileNameFrom(response.headers.get('content-disposition'), fallback),
      mimeType: blob.type || response.headers.get('content-type') || 'application/octet-stream',
      data: blob,
      anchor: options.anchor ?? null,
    })
  } catch (error) {
    toast.show({ title: SAVE_FILE_MESSAGES.fetchFailed, description: (error as Error).message, tone: 'danger', id: 'save-file' })
    return 'failed'
  }
}

export interface DownloadLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'download'> {
  /** Adresse auf dem eigenen Server, z. B. /api/files/<id> */
  href: string
  /** vorgeschlagener Dateiname; ohne: der Name, den der Server nennt */
  fileName?: string | null
  children: ReactNode
}

/**
 * Verweis auf eine Datei des Servers: im Browser ein gewöhnlicher Download-Verweis, in der App holt ein Tipp die
 * Datei mit der Sitzung und übergibt sie ans Teilen-Blatt (§4.2, DATEV-Export).
 */
export const DownloadLink = forwardRef<HTMLAnchorElement, DownloadLinkProps>(function DownloadLink(
  { href, fileName, onClick, children, ...rest },
  ref,
) {
  const handle = (event: MouseEvent<HTMLAnchorElement>): void => {
    onClick?.(event)
    if (event.defaultPrevented || !nativeApp()) return
    event.preventDefault()
    const anchor = event.currentTarget
    void saveUrl(href, { ...(fileName ? { fileName } : {}), anchor })
  }
  return (
    <a ref={ref} href={href} download={fileName ?? ''} onClick={handle} {...rest}>
      {children}
    </a>
  )
})

/**
 * Seite in einem neuen Tab öffnen (⌘/Strg-Klick in Listen). In der App gibt es keine neuen Fenster: iOS öffnete
 * jedes `_blank` in Safari – ohne Sitzung (§5.2). Dort öffnet `navigate` dieselbe Seite im selben Fenster.
 */
export function openInNewTab(href: string, navigate: (href: string) => void): void {
  if (!openInNewWindow(href)) navigate(href)
}
